import axios from 'axios';
import { getConfigSync } from './config';

// Track if we're already redirecting to prevent loops
let isRedirecting = false;

// Get API URL and set as default baseURL
const config = getConfigSync();
const origin = typeof window !== 'undefined' ? window.location.origin : '';
const isLocalhost = origin.includes('localhost') || origin.includes('127.0.0.1');

// Configure global axios defaults
axios.defaults.timeout = 10000;
axios.defaults.headers.common['Content-Type'] = 'application/json';
axios.defaults.withCredentials = true;

// Set baseURL based on environment
// Axios requires absolute URLs for baseURL to work correctly
// In development (localhost), use absolute URL with backend port
// In production, use same origin + /api (Nginx will proxy /api to backend)
if (isLocalhost) {
  // Development: use absolute URL with backend port
  axios.defaults.baseURL = 'http://localhost:43001/api';
} else {
  // Production: use same origin + /api
  // Nginx will proxy /api requests to backend, so we use frontend origin
  // This ensures cookies and CORS work correctly
  axios.defaults.baseURL = `${origin}/api`;
}
console.log('[AxiosSetup] Set default baseURL:', axios.defaults.baseURL, { isLocalhost, origin });

// CRITICAL FIX: Add global request interceptor to force absolute URLs for localhost only
axios.interceptors.request.use(
  (config) => {
    // Only force absolute URL for localhost/127.0.0.1 (development)
    // In production, use relative paths which Nginx will proxy
    const origin = typeof window !== 'undefined' ? window.location.origin : '';
    const isLocalhost = origin.includes('localhost') || origin.includes('127.0.0.1');
    
    if (isLocalhost) {
      // Development: Force absolute URL with correct backend port
      // CRITICAL: If URL is already absolute but uses wrong port (43000), fix it
      if (config.url && config.url.includes('localhost:43000')) {
        config.url = config.url.replace('localhost:43000', 'localhost:43001');
        console.log('[AxiosSetup] Fixed URL port from 43000 to 43001:', config.url);
      } else if (config.url && config.url.startsWith('/')) {
        // If URL is relative, set baseURL to absolute
        config.baseURL = 'http://localhost:43001/api';
        console.log('[AxiosSetup] Set absolute baseURL for relative URL:', config.baseURL);
      } else if (config.url && !config.url.startsWith('http')) {
        // URL is not absolute and not relative - set baseURL
        config.baseURL = 'http://localhost:43001/api';
        console.log('[AxiosSetup] Set absolute baseURL:', config.baseURL);
      } else if (!config.baseURL || config.baseURL.startsWith('/') || config.baseURL.includes('localhost:43000')) {
        // No baseURL, relative baseURL, or baseURL with wrong port - fix it
        if (config.baseURL && config.baseURL.includes('localhost:43000')) {
          config.baseURL = config.baseURL.replace('localhost:43000', 'localhost:43001');
          console.log('[AxiosSetup] Fixed baseURL port from 43000 to 43001:', config.baseURL);
        } else {
          config.baseURL = 'http://localhost:43001/api';
          console.log('[AxiosSetup] Set absolute baseURL:', config.baseURL);
        }
      }
    } else {
      // Production: Ensure baseURL uses same origin
      // If baseURL is not set or is relative, set it to absolute URL with same origin
      if (!config.baseURL || config.baseURL.startsWith('/')) {
        config.baseURL = `${origin}/api`;
        console.log('[AxiosSetup] Set baseURL in production:', config.baseURL);
      }
      // If baseURL uses wrong origin or port, fix it
      else if (config.baseURL && !config.baseURL.includes(origin.split(':')[0])) {
        config.baseURL = `${origin}/api`;
        console.log('[AxiosSetup] Fixed baseURL in production:', config.baseURL);
      }
      // If URL is absolute with wrong origin, make it relative
      if (config.url && config.url.startsWith('http') && !config.url.includes(origin)) {
        // Extract path from absolute URL
        try {
          const urlObj = new URL(config.url);
          config.url = urlObj.pathname + urlObj.search;
          console.log('[AxiosSetup] Converted absolute URL to relative:', config.url);
        } catch (e) {
          // Invalid URL, keep as is
        }
      }
    }

    // Callers often pass `${API_URL}/apis` while baseURL is already `.../api`.
    // Axios then requests /api/api/apis; nginx serves the SPA HTML as 200.
    const resolvedBase = String(config.baseURL || axios.defaults.baseURL || '').replace(/\/+$/, '');
    const requestUrl = String(config.url || '');
    if (
      resolvedBase.endsWith('/api') &&
      requestUrl &&
      !requestUrl.startsWith('http') &&
      (requestUrl === '/api' || requestUrl.startsWith('/api/'))
    ) {
      config.url = requestUrl.slice(4) || '/';
    }
    
    // Let the browser set multipart boundary — never send application/json for FormData uploads
    if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
      const stripContentType = (headers: Record<string, unknown> | undefined) => {
        if (!headers) return;
        if (typeof (headers as any).delete === 'function') {
          (headers as any).delete('Content-Type');
          (headers as any).delete('content-type');
        } else {
          delete headers['Content-Type'];
          delete headers['content-type'];
        }
      };
      stripContentType(config.headers as Record<string, unknown>);
      if (config.headers?.common) {
        stripContentType(config.headers.common as Record<string, unknown>);
      }
      config.transformRequest = [(data, headers) => {
        stripContentType(headers as Record<string, unknown>);
        return data;
      }];
    }

    // Log the actual URL being used
    const fullUrl = config.url && config.url.startsWith('http')
      ? config.url
      : (config.baseURL 
          ? (config.baseURL.endsWith('/') ? config.baseURL.slice(0, -1) : config.baseURL) + 
            (config.url?.startsWith('/') ? config.url : '/' + (config.url || ''))
          : config.url);
    console.log('[AxiosSetup] Global request URL:', fullUrl, { baseURL: config.baseURL, url: config.url, origin });
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add global axios interceptor for 401 errors
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401 && !isRedirecting) {
      isRedirecting = true;
      // Only redirect if we're not already on the login page
      const currentPath = window.location.pathname;
      if (!currentPath.includes('/login')) {
        // Use window.location.href for a full page reload to clear any state
        window.location.href = '/login';
      } else {
        // If already on login page, just reset the flag
        setTimeout(() => {
          isRedirecting = false;
        }, 1000);
      }
    }
    return Promise.reject(error);
  }
);

export default axios;
